import {
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  Divider,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Select,
  MenuItem,
  Modal,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import RouteIcon from "@mui/icons-material/Route";
import PersonIcon from "@mui/icons-material/Person";
import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { PROJECT_TERMINOLOGY } from "../enums";
import NavigationHeader from "../../../components/NavigationHeader";
import { ProjectTabEnum } from "../enums";
import {
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
  tableHeaderStyles,
  tableCellStyles,
} from "../../../utils/styles";
import { useToast } from "../../../components/Toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  projectLogApiService,
  superProjectLogApiService,
} from "../services/projectLogApiService";
import { Loading } from "../../../components/Loading";
import InfoIcon from "@mui/icons-material/Info";
import { DocumentUploadSection } from "../../../components/DocumentUploadSection";
import { UploadedFile } from "../../../components/FileUploadSection";
import { uploadFile } from "../../../utils/UploadFile";
import { Note } from "@mui/icons-material";
import { formatText } from "../../../utils/utils";
import ReAllocateModal from "../components/ReAllocateModal";
import { renderBasicLogInfoByVehicle } from "./ProjectLogsByVehicle";
import { ProjectDetailTabs } from "../types";

// New types for staff attendance array structure
interface ReallocatedData {
  isReplaced: boolean;
  numberOfDays?: number;
  reason?: string;
}

interface StaffAttendanceItem {
  id: string;
  status: "PRESENT" | "ABSENT";
  isReallocated: boolean;
  isReplacement: boolean; // true for newly added staff
  reallocatedData?: ReallocatedData;
  originalStaffData?: any; // Store original staff data for cancellation
  staffData: any; // Current staff data (original or new)
  isOldStaff: boolean;
}

// Types for the API response
interface ProjectLogData {
  allocation: {
    id: string;
    category: string;
    categoryId: string;
    subCategory: string;
    status: string;
    startDate: string;
    endDate: string | null;
    meta: any;
    projectId: string;
    companyId: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  vehicle: {
    id: string;
    status: string;
    companyId: string;
    vehicleType: string;
    acquisitionType: string;
    vehicleNumber: string;
    chassisNumber: string;
    engineNumber: string | null;
    makeYear: number;
    model: string;
    color: string;
    bodyType: string;
    configuration: string;
    fuelType: string;
    seatingCapacity: number;
    isAC: boolean;
    firstServiceDue: string | null;
    maintenanceInterval: string;
    nextMaintenanceDate: string | null;
    warrantyPeriod: string | null;
    lastServiceDate: string | null;
    lastServiceOdometer: string | null;
    knownMaintenanceIssues: string | null;
    vehicleCost: number | null;
    isFinanced: boolean;
    insuranceType: string | null;
    insuranceProvider: string | null;
    insurancePolicyNumber: string | null;
    insuranceStartDate: string | null;
    insuranceEndDate: string | null;
    insurancePremium: number | null;
    fastagNumber: string | null;
    fastagProvider: string | null;
    rcNumber: string | null;
    rcExpiryDate: string;
    pucNumber: string | null;
    pucStartDate: string | null;
    pucEndDate: string | null;
    fitnessExpiryDate: string | null;
    pollutionExpiryDate: string | null;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  staffMembers: Array<{
    id: string;
    companyId: string;
    name: string;
    contactNumber: string;
    alternateNumber: string;
    address: string;
    type: string;
    status: string;
    employmentType: string;
    joiningDate: string;
    salary: number;
    aadharNumber: string;
    drivingLicense: string;
    referredByName: string;
    referredByContact: string;
    reference1Name: string;
    reference1Contact: string;
    reference2Name: string;
    reference2Contact: string;
    photoUrl: string | null;
    notes: string;
    createdAt: string;
    updatedAt: string;
    allocationTagId?: string;
  }>;
  startTime: string;
  endTime: string;
}

const AddProjectLogByVehicle = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [projectData, setProjectData] = useState<ProjectLogData | null>(null);
  const { showToast } = useToast();
  const { id: projectId, superProjectId } = useParams();
  const localUser = sessionStorage.getItem("user");
  const user = localUser ? JSON.parse(localUser) : null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get("vehicleId");
  const allocationId = searchParams.get("allocationId");
  const isDivision = searchParams.get("isDivision") === "true";
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);

  // Function to update staff members in projectData
  const updateStaffMembers = (
    oldStaffId: string,
    newStaff: any,
    isReplacement: boolean = false,
    numberOfDays?: number,
    reason?: string
  ) => {
    setProjectData((prev) => {
      if (!prev) return prev;

      let updatedStaffMembers = [...prev.staffMembers];

      if (isReplacement) {
        // For replacement, remove the old staff completely
        updatedStaffMembers = updatedStaffMembers.filter(
          (staff) => staff.id !== oldStaffId
        );

        // Add the new staff
        if (newStaff) {
          updatedStaffMembers.push(newStaff);
        }
      } else {
        // For temporary allocation, keep the old staff but mark them absent
        // and add the new staff
        if (newStaff) {
          updatedStaffMembers.push(newStaff);
        }
      }

      return {
        ...prev,
        staffMembers: updatedStaffMembers,
      };
    });
  };
  const fetchDataForProjectLog = async () => {
    try {
      setIsLoading(true);
      const data = await superProjectLogApiService.getDataForSuperProjectLog(
        projectId!,
        vehicleId!
      );
      setProjectData(data);
    } catch (error) {
      console.error("Error fetching project data:", error);
      showToast("Failed to load project data. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (vehicleId && projectId) {
      fetchDataForProjectLog();
    }
  }, [vehicleId, projectId]);

  // Create validation schema based on actual staff data
  const createValidationSchema = (staffMembers: any[]) => {
    return Yup.object().shape({
      staffAttendances: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required(),
          status: Yup.string()
            .oneOf(["PRESENT", "ABSENT"], "Please select attendance status")
            .required("Attendance status is required"),
          isReallocated: Yup.boolean().required(),
          isReplacement: Yup.boolean().required(),
          reallocatedData: Yup.mixed().nullable(),
          originalStaffData: Yup.mixed().nullable(),
          staffData: Yup.object().required(),
        })
      ),
      startTime: Yup.string().required("Start time is required"),
      endTime: Yup.string()
        .required("End time is required")
        .test(
          "end-time-after-start-time",
          "End time must be after start time",
          (value, context) => {
            const startTime = context.parent.startTime;
            if (!startTime) return true;
            const [startHours, startMinutes] = startTime.split(":").map(Number);
            const [endHours, endMinutes] = value.split(":").map(Number);
            return (
              endHours > startHours ||
              (endHours === startHours && endMinutes > startMinutes)
            );
          }
        ),
      startOdometer: Yup.number()
        .typeError("Start odometer must be a number")
        .positive("Start odometer must be positive")
        .optional(),
      endOdometer: Yup.number()
        .typeError("End odometer must be a number")
        .positive("End odometer must be positive")
        .test(
          "end-odometer-after-start-odometer",
          "End odometer must be greater than start odometer",
          (value, context) => {
            const startOdometer = context.parent.startOdometer;
            if (!startOdometer) return true;
            return !value || value > startOdometer;
          }
        )
        .optional(),
      dateOfEntry: Yup.date().required("Date of entry is required"),
      capacity: Yup.number().optional(),
      occupancy: Yup.number()
        .optional()
        .min(0, "Occupancy cannot be negative")
        .test(
          "occupancy-less-than-capacity",
          "Occupancy must be less than vehicle capacity",
          (value, context) => {
            const capacity = context.parent.capacity;
            if (!capacity) return true;
            return !value || value <= capacity;
          }
        ),
    });
  };

  // Create initial values based on actual data
  const createInitialValues = (data: ProjectLogData) => {
    return {
      staffAttendances:
        data?.staffMembers?.map(
          (staff): StaffAttendanceItem => ({
            id: staff.id,
            status: "PRESENT" as "PRESENT" | "ABSENT",
            isReallocated: false,
            isOldStaff: true,
            isReplacement: false,
            staffData: staff,
            reallocatedData: undefined,
            originalStaffData: undefined,
          })
        ) || [],
      startTime: "",
      endTime: "",
      startOdometer: "",
      endOdometer: "",
      dateOfEntry: new Date().toISOString().split("T")[0],
      companyId: user?.companyId,
      createdBy: user?.id,
      projectId,
      vehicleId: data?.vehicle?.id,
      capacity: data?.vehicle?.seatingCapacity,
      occupancy: 0,
      notes: "",
    };
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const response = await projectLogApiService.createProjectLog(values);
      if (response) {
        if (selectedFiles && selectedFiles?.length > 0) {
          const uploadPromises = selectedFiles.map((file: File) =>
            uploadFile({
              file: file,
              documentType: "project-log",
              documentTypeId: response?.id,
              documentContext: "project-log",
              description: "Project log",
            })
          );
          await Promise.all(uploadPromises);
        }
        showToast("Log submitted successfully!", "success");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error submitting log:", error);
      showToast("Failed to submit log. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Container sx={{ p: 1 }}>
        <NavigationHeader
          title={"Add Log"}
          breadcrumbs={[
            {
              label: PROJECT_TERMINOLOGY.PLURAL,
              path: superProjectId
                ? `/super`
                : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
            },
            ...(superProjectId
              ? [
                  {
                    label: ProjectTabEnum.DIVISIONS.label,
                    path: `/super/${superProjectId}?isSuperProject=true&tab=${ProjectDetailTabs.DIVISIONS}`,
                  },
                  {
                    label: `${ProjectTabEnum.DIVISIONS.label} Details`,
                    path: `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}`,
                  },
                ]
              : []),
            {
              label: ProjectTabEnum.LOGS.label,
              path: superProjectId
                ? `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`
                : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`,
            },
            {
              label: "Add Log",
            },
          ]}
        />
        <Loading message="Loading project log..." />
      </Container>
    );
  }

  if (!projectData) {
    return (
      <Container sx={{ p: 1 }}>
        <NavigationHeader
          title={"Add Log"}
          breadcrumbs={[
            {
              label: PROJECT_TERMINOLOGY.PLURAL,
              path: superProjectId
                ? `/super`
                : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
            },
            ...(superProjectId
              ? [
                  {
                    label: ProjectTabEnum.DIVISIONS.label,
                    path: `/super/${superProjectId}?isSuperProject=true&tab=${ProjectDetailTabs.DIVISIONS}`,
                  },
                  {
                    label: `${ProjectTabEnum.DIVISIONS.label} Details`,
                    path: `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}`,
                  },
                ]
              : []),
            {
              label: ProjectTabEnum.LOGS.label,
              path: superProjectId
                ? `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`
                : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`,
            },
            {
              label: "Add Log",
            },
          ]}
        />
        <Typography variant="body1" color="error">
          No project data available. Please try again.
        </Typography>
      </Container>
    );
  }

  const initialValues = createInitialValues(projectData);

  return (
    <Container sx={{ p: 1 }}>
      <NavigationHeader
        title={"Add Log"}
        breadcrumbs={[
          {
            label: PROJECT_TERMINOLOGY.PLURAL,
            path: superProjectId
              ? `/super`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
          },
          ...(superProjectId
            ? [
                {
                  label: ProjectTabEnum.DIVISIONS.label,
                  path: `/super/${superProjectId}?isSuperProject=true&tab=${ProjectDetailTabs.DIVISIONS}`,
                },
                {
                  label: `${ProjectTabEnum.DIVISIONS.label} Details`,
                  path: `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}`,
                },
              ]
            : []),
          {
            label: ProjectTabEnum.LOGS.label,
            path: superProjectId
              ? `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`,
          },
          {
            label: "Add Log",
          },
        ]}
      />

      <Box sx={{ mb: 1 }}>
        {renderBasicLogInfoByVehicle(projectData?.vehicle)}
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={createValidationSchema(
          projectData?.staffMembers || []
        )}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          isValid,
          setFieldValue,
        }) => {
          console.log("error", errors);
          // Update the updateStaffMembers function to also sync with current Formik context
          const enhancedUpdateStaffMembers = (
            oldStaffId: string,
            newStaff: any,
            isReplacement: boolean = false,
            numberOfDays?: number,
            reason?: string
          ) => {
            // Update projectData
            updateStaffMembers(
              oldStaffId,
              newStaff,
              isReplacement,
              numberOfDays,
              reason
            );

            // Update Formik staffAttendances
            const currentAttendances = [...values.staffAttendances];

            // Find the old staff attendance item
            const oldStaffIndex = currentAttendances.findIndex(
              (item) => item.id === oldStaffId
            );

            if (oldStaffIndex !== -1) {
              const oldStaffItem = currentAttendances[oldStaffIndex];

              if (isReplacement) {
                // For full replacement, remove old staff completely and add new staff
                currentAttendances.splice(oldStaffIndex, 1);

                // Add new staff as present
                if (newStaff) {
                  currentAttendances.push({
                    id: newStaff.id,
                    status: "PRESENT" as "PRESENT" | "ABSENT",
                    isReallocated: true,
                    isOldStaff: false,
                    isReplacement: true,
                    reallocatedData: {
                      isReplaced: true,
                      numberOfDays,
                      reason,
                    },
                    originalStaffData: oldStaffItem.staffData,
                    staffData: newStaff,
                  } as StaffAttendanceItem);
                }
              } else {
                // For temporary allocation, mark old staff as absent and add new staff
                currentAttendances[oldStaffIndex] = {
                  ...oldStaffItem,
                  status: "ABSENT" as "PRESENT" | "ABSENT",
                  isReallocated: true,
                  isOldStaff: true,
                } as StaffAttendanceItem;

                // Add new staff as present
                if (newStaff) {
                  currentAttendances.push({
                    id: newStaff.id,
                    status: "PRESENT" as "PRESENT" | "ABSENT",
                    isReallocated: true,
                    isOldStaff: false,
                    isReplacement: true,
                    reallocatedData: {
                      isReplaced: false,
                      numberOfDays,
                      reason,
                    },
                    originalStaffData: oldStaffItem.staffData,
                    staffData: newStaff,
                  } as StaffAttendanceItem);
                }
              }
            }

            setFieldValue("staffAttendances", currentAttendances);
          };
          return (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card elevation={2} sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ px: 2, py: 1 }}>
                      <Grid container spacing={3}>
                        {/* Required Fields */}
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              borderRadius: 2,
                              bgcolor: "background.paper",
                            }}
                          >
                            <Grid container spacing={2}>
                              <Grid item container md={6} spacing={2}>
                                <Grid item xs={12}>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <RouteIcon
                                      color="primary"
                                      sx={{ fontSize: "0.75rem" }}
                                    />
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      Trip Information
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Field
                                    name="dateOfEntry"
                                    as={TextField}
                                    label="Trip Date"
                                    type="date"
                                    fullWidth
                                    required
                                    {...commonDateFieldProps}
                                  />
                                </Grid>
                                <Grid item xs={6} sm={6} md={3}>
                                  <Field
                                    name="startTime"
                                    as={TextField}
                                    label="Start Time"
                                    type="time"
                                    fullWidth
                                    error={
                                      !!errors.startTime && touched.startTime
                                    }
                                    helperText={
                                      errors.startTime && touched.startTime
                                        ? errors.startTime
                                        : ""
                                    }
                                    {...commonDateFieldProps}
                                    required
                                  />
                                </Grid>
                                <Grid item xs={6} sm={6} md={3}>
                                  <Field
                                    name="endTime"
                                    as={TextField}
                                    label="End Time"
                                    type="time"
                                    fullWidth
                                    error={!!errors.endTime && touched.endTime}
                                    helperText={
                                      errors.endTime && touched.endTime
                                        ? errors.endTime
                                        : ""
                                    }
                                    {...commonDateFieldProps}
                                    required
                                  />
                                </Grid>
                              </Grid>
                              <Grid item container md={6} spacing={2}>
                                <Grid item xs={12}>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <InfoIcon
                                      color="primary"
                                      sx={{ fontSize: "0.75rem" }}
                                    />
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      Optional Information
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={6} sm={6} md={3}>
                                  <Field
                                    name="startOdometer"
                                    as={TextField}
                                    label="Start KMs"
                                    type="number"
                                    fullWidth
                                    inputProps={{
                                      min: 0,
                                      step: 1,
                                      onWheel: (
                                        e: React.WheelEvent<HTMLInputElement>
                                      ) =>
                                        (e.target as HTMLInputElement).blur(),
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Typography
                                            variant="body2"
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            KMs
                                          </Typography>
                                        </InputAdornment>
                                      ),
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    size={commonTextFieldProps.size}
                                    sx={{
                                      ...commonTextFieldProps.sx,
                                      "& input[type=number]": {
                                        MozAppearance: "textfield",
                                      },
                                      "& input[type=number]::-webkit-outer-spin-button":
                                        {
                                          WebkitAppearance: "none",
                                          margin: 0,
                                        },
                                      "& input[type=number]::-webkit-inner-spin-button":
                                        {
                                          WebkitAppearance: "none",
                                          margin: 0,
                                        },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={6} sm={6} md={3}>
                                  <Field
                                    name="endOdometer"
                                    as={TextField}
                                    label="End KMs"
                                    type="number"
                                    fullWidth
                                    inputProps={{
                                      min: 0,
                                      step: 1,
                                      onWheel: (
                                        e: React.WheelEvent<HTMLInputElement>
                                      ) =>
                                        (e.target as HTMLInputElement).blur(),
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Typography
                                            variant="body2"
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            KMs
                                          </Typography>
                                        </InputAdornment>
                                      ),
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    size={commonTextFieldProps.size}
                                    sx={{
                                      ...commonTextFieldProps.sx,
                                      "& input[type=number]": {
                                        MozAppearance: "textfield",
                                      },
                                      "& input[type=number]::-webkit-outer-spin-button":
                                        {
                                          WebkitAppearance: "none",
                                          margin: 0,
                                        },
                                      "& input[type=number]::-webkit-inner-spin-button":
                                        {
                                          WebkitAppearance: "none",
                                          margin: 0,
                                        },
                                    }}
                                  />
                                </Grid>
                                {/* <Grid item xs={12} sm={6} md={3}>
                                  <Field
                                    name="occupancy"
                                    as={TextField}
                                    label="Occupancy"
                                    type="number"
                                    fullWidth
                                    inputProps={{
                                      min: 0,
                                      step: 1,
                                      onWheel: (
                                        e: React.WheelEvent<HTMLInputElement>
                                      ) =>
                                        (e.target as HTMLInputElement).blur(),
                                    }}
                                    InputProps={{
                                      ...(values.vehicleId
                                        ? {
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                <Typography variant="body2">
                                                  /{values.capacity || 0}
                                                </Typography>
                                              </InputAdornment>
                                            ),
                                          }
                                        : {}),
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    error={
                                      !!errors.occupancy && touched.occupancy
                                    }
                                    helperText={
                                      errors.occupancy && touched.occupancy
                                        ? errors.occupancy
                                        : ""
                                    }
                                    size={commonTextFieldProps.size}
                                    sx={{
                                      ...commonTextFieldProps.sx,
                                      "& input[type=number]": {
                                        MozAppearance: "textfield",
                                      },
                                      "& input[type=number]::-webkit-outer-spin-button":
                                        {
                                          WebkitAppearance: "none",
                                          margin: 0,
                                        },
                                      "& input[type=number]::-webkit-inner-spin-button":
                                        {
                                          WebkitAppearance: "none",
                                          margin: 0,
                                        },
                                    }}
                                  />
                                </Grid> */}
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                        {/* Staff Attendance Section */}
                        {projectData?.staffMembers &&
                          projectData?.staffMembers?.length > 0 && (
                            <Grid item xs={12} md={6}>
                              <Box
                                sx={{
                                  borderRadius: 2,
                                  bgcolor: "background.paper",
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  mb={2}
                                >
                                  <PersonIcon
                                    color="primary"
                                    sx={{ fontSize: "1rem" }}
                                  />
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    Staff Attendance
                                  </Typography>
                                </Box>
                                <TableContainer
                                  component={Paper}
                                  elevation={0}
                                  sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: "grey.50" }}>
                                        <TableCell sx={tableHeaderStyles}>
                                          Staff
                                        </TableCell>
                                        <TableCell sx={tableHeaderStyles}>
                                          Attendance
                                        </TableCell>
                                        <TableCell sx={tableHeaderStyles}>
                                          Change
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {values.staffAttendances.map(
                                        (
                                          attendanceItem: StaffAttendanceItem,
                                          index: number
                                        ) => {
                                          const staff =
                                            attendanceItem.staffData;
                                          const canReallocate =
                                            !attendanceItem.isReallocated;
                                          const isReplacement =
                                            attendanceItem.isReplacement;
                                          const isFullyReplaced =
                                            attendanceItem.reallocatedData
                                              ?.isReplaced;

                                          return (
                                            <TableRow
                                              key={attendanceItem.id}
                                              sx={{
                                                "&:hover": {
                                                  bgcolor: "grey.50",
                                                },
                                                ...(attendanceItem.isReallocated &&
                                                  !isReplacement && {
                                                    opacity: 0.6,
                                                    backgroundColor: "grey.100",
                                                  }),
                                              }}
                                            >
                                              <TableCell sx={tableCellStyles}>
                                                {staff.name} <br />(
                                                {formatText(staff.type)}
                                                {`${
                                                  staff.contactNumber
                                                    ? ` / ${staff.contactNumber}`
                                                    : ""
                                                }`}
                                                )
                                              </TableCell>
                                              <TableCell sx={tableCellStyles}>
                                                <Box
                                                  display="flex"
                                                  alignItems="center"
                                                  gap={1}
                                                >
                                                  {attendanceItem.status ===
                                                  "PRESENT"
                                                    ? "Present"
                                                    : "Absent"}
                                                  <Switch
                                                    checked={
                                                      attendanceItem.status ===
                                                      "PRESENT"
                                                    }
                                                    onChange={(e) => {
                                                      const value = e.target
                                                        .checked
                                                        ? "PRESENT"
                                                        : "ABSENT";

                                                      const updatedAttendances =
                                                        [
                                                          ...values.staffAttendances,
                                                        ];
                                                      updatedAttendances[
                                                        index
                                                      ] = {
                                                        ...updatedAttendances[
                                                          index
                                                        ],
                                                        status: value as
                                                          | "PRESENT"
                                                          | "ABSENT",
                                                      };
                                                      setFieldValue(
                                                        "staffAttendances",
                                                        updatedAttendances
                                                      );
                                                    }}
                                                    color="success"
                                                    size="small"
                                                    disabled={
                                                      attendanceItem.isReallocated &&
                                                      !isReplacement
                                                    }
                                                  />
                                                </Box>
                                              </TableCell>
                                              <TableCell sx={tableCellStyles}>
                                                <Box
                                                  display="flex"
                                                  gap={1}
                                                  flexDirection="column"
                                                >
                                                  {attendanceItem.isOldStaff &&
                                                    (canReallocate ? (
                                                      <Typography
                                                        color="primary"
                                                        variant="body2"
                                                        sx={{
                                                          fontSize: "0.65rem",
                                                          fontStyle: "italic",
                                                          cursor: "pointer",
                                                          "&:hover": {
                                                            textDecoration:
                                                              "underline",
                                                          },
                                                          textDecoration:
                                                            "underline",
                                                        }}
                                                        onClick={() => {
                                                          setIsModalOpen(true);
                                                          setSelectedStaff(
                                                            staff
                                                          );
                                                        }}
                                                      >
                                                        Change
                                                      </Typography>
                                                    ) : (
                                                      <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                          fontSize: "0.75rem",
                                                        }}
                                                      >
                                                        {isFullyReplaced
                                                          ? "Fully Replaced"
                                                          : "Temporarily Absent"}
                                                      </Typography>
                                                    ))}

                                                  {/* Cancel Reallocation Button - Show for replacement staff or reallocated original staff */}
                                                  {attendanceItem.isReallocated &&
                                                    !attendanceItem.isOldStaff && (
                                                      <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => {
                                                          const updatedAttendances =
                                                            [
                                                              ...values.staffAttendances,
                                                            ];

                                                          // This is a replacement staff - remove it and restore original
                                                          const originalStaffId =
                                                            attendanceItem
                                                              .originalStaffData
                                                              ?.id;

                                                          // Remove this replacement staff first
                                                          updatedAttendances.splice(
                                                            index,
                                                            1
                                                          );

                                                          if (originalStaffId) {
                                                            // Check if original staff exists in the list
                                                            const originalIndex =
                                                              updatedAttendances.findIndex(
                                                                (item) =>
                                                                  item.id ===
                                                                  originalStaffId
                                                              );

                                                            if (
                                                              originalIndex !==
                                                              -1
                                                            ) {
                                                              // Original staff exists, restore it
                                                              updatedAttendances[
                                                                originalIndex
                                                              ] = {
                                                                id: originalStaffId,
                                                                status:
                                                                  "PRESENT" as
                                                                    | "PRESENT"
                                                                    | "ABSENT",
                                                                isReallocated:
                                                                  false,
                                                                isOldStaff:
                                                                  true,
                                                                isReplacement:
                                                                  false,
                                                                staffData:
                                                                  attendanceItem.originalStaffData!,
                                                                reallocatedData:
                                                                  undefined,
                                                                originalStaffData:
                                                                  undefined,
                                                              };
                                                            } else {
                                                              // Original staff was completely removed (full replacement case)
                                                              // Add it back to the list
                                                              updatedAttendances.push(
                                                                {
                                                                  id: originalStaffId,
                                                                  status:
                                                                    "PRESENT" as
                                                                      | "PRESENT"
                                                                      | "ABSENT",
                                                                  isReallocated:
                                                                    false,
                                                                  isOldStaff:
                                                                    true,
                                                                  isReplacement:
                                                                    false,
                                                                  staffData:
                                                                    attendanceItem.originalStaffData!,
                                                                  reallocatedData:
                                                                    undefined,
                                                                  originalStaffData:
                                                                    undefined,
                                                                }
                                                              );
                                                            }
                                                          }

                                                          setFieldValue(
                                                            "staffAttendances",
                                                            updatedAttendances
                                                          );
                                                        }}
                                                        {...smallButtonProp}
                                                      >
                                                        Cancel Reallocation
                                                      </Button>
                                                    )}
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        }
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            </Grid>
                          )}

                        <Grid item xs={12}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={2}
                          >
                            <Note color="primary" sx={{ fontSize: "1rem" }} />
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                            >
                              Notes
                            </Typography>
                          </Box>
                          <Field
                            name="notes"
                            as={TextField}
                            label="Notes"
                            fullWidth
                            multiline
                            rows={2}
                            {...commonTextFieldProps}
                          />
                        </Grid>
                        {/* Upload Section */}
                        <Grid item xs={12}>
                          <DocumentUploadSection
                            title="Upload Files"
                            selectedFiles={selectedFiles}
                            uploadedFiles={uploadedFiles}
                            onFileSelect={(files: File[]) =>
                              setSelectedFiles((prev) => [...prev, ...files])
                            }
                            onFileRemove={(index: number) =>
                              setSelectedFiles((prev) =>
                                prev.filter((_, i) => i !== index)
                              )
                            }
                            onPreview={(url) => {
                              window.open(url, "_blank");
                            }}
                            isLoading={isLoading}
                          />
                        </Grid>
                      </Grid>

                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={2}
                        mt={2}
                      >
                        <Button
                          type="button"
                          variant="outlined"
                          color="error"
                          onClick={() => navigate(-1)}
                          {...smallButtonProp}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={isSubmitting}
                          {...smallButtonProp}
                        >
                          {isSubmitting ? "Submitting..." : "Save Log"}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <ReAllocateModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                projectId={projectId!}
                allocationId={allocationId!}
                selectedStaff={selectedStaff}
                searchableEntity={selectedStaff?.type}
                updateStaffMembers={enhancedUpdateStaffMembers}
              />
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default AddProjectLogByVehicle;
